import React from "react";

type GlobalSearchActions = {
  openSearch: () => void;
  closeSearch: () => void;
};

const GlobalSearchStateContext = React.createContext<boolean>(false);
const GlobalSearchActionsContext =
  React.createContext<GlobalSearchActions | null>(null);

/** Subscribe to the open/closed state of the global search dialog. */
export function useGlobalSearchOpen() {
  return React.useContext(GlobalSearchStateContext);
}

/** Get stable open/close callbacks without subscribing to the open state. */
export function useGlobalSearchActions() {
  const ctx = React.useContext(GlobalSearchActionsContext);
  if (!ctx) {
    throw new Error(
      "useGlobalSearchActions must be used within GlobalSearchProvider",
    );
  }
  return ctx;
}

export function GlobalSearchProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(false);

  const openSearch = React.useCallback(() => setOpen(true), []);
  const closeSearch = React.useCallback(() => setOpen(false), []);

  const actions = React.useMemo(
    () => ({ openSearch, closeSearch }),
    [openSearch, closeSearch],
  );

  return (
    <GlobalSearchActionsContext.Provider value={actions}>
      <GlobalSearchStateContext.Provider value={open}>
        {children}
      </GlobalSearchStateContext.Provider>
    </GlobalSearchActionsContext.Provider>
  );
}
